import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import api, { BACKEND_URL } from '../api/axios';
import Navbar from './Navbar';
import Footer from './Footer';
import EmployerSidebar, { ChevronDownIcon } from './EmployerSidebar';

const GRADIENT = 'bg-gradient-to-r from-[#5B2EFF] via-[#8B5CF6] to-[#FF4D8D]';

const BENEFIT_OPTIONS = [
  'Work From Home', 'Health Insurance', 'Free Food', 'Learning Budget', 'Paid Time Off',
  'Remote Work', 'Stock Options', 'Gym Membership', 'Flexible Hours', 'Parental Leave',
];

const INPUT =
  'w-full border border-gray-200 rounded-xl px-3.5 py-2.5 text-sm text-[#0D1B3D] bg-white focus:outline-none focus:ring-2 focus:ring-[#8B5CF6] focus:border-transparent transition';

function getInitials(name) {
  if (!name) return '?';
  return name.trim().split(/\s+/).slice(0, 2).map((w) => w[0]?.toUpperCase()).join('');
}

function resolveMediaUrl(path) {
  if (!path) return null;
  return path.startsWith('http') ? path : `${BACKEND_URL}/storage/${path}`;
}

function Field({ label, hint, children }) {
  return (
    <label className="block">
      <span className="block text-sm font-semibold text-gray-700 mb-1.5">{label}</span>
      {children}
      {hint && <span className="block text-xs text-gray-400 mt-1">{hint}</span>}
    </label>
  );
}

function SectionCard({ title, description, children }) {
  return (
    <section className="bg-white border border-purple-100 rounded-2xl shadow-sm p-5 sm:p-6">
      <div className="mb-5">
        <h2 className="text-base font-bold text-[#0D1B3D]">{title}</h2>
        {description && <p className="text-sm text-gray-500 mt-0.5">{description}</p>}
      </div>
      <div className="space-y-4">{children}</div>
    </section>
  );
}

function CompanyProfile() {
  const navigate = useNavigate();
  const [user] = useState(() => {
    const stored = localStorage.getItem('user');
    return stored ? JSON.parse(stored) : null;
  });

  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [logoError, setLogoError] = useState(false);
  const [coverError, setCoverError] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingCover, setUploadingCover] = useState(false);
  const [showFiltersMobile, setShowFiltersMobile] = useState(false);

  const [formData, setFormData] = useState({
    name: '', company_website: '', company_about: '', company_gst: '', company_size: '',
    company_industry: '', company_founded_year: '', company_headquarters: '',
    // branch_offices has no backend column/validation rule yet (see hint under the field).
    branch_offices: '', company_culture: '', company_linkedin: '', company_facebook: '',
    company_instagram: '', company_benefits: [],
  });

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }
    const fetchProfile = async () => {
      try {
        const { data } = await api.get('/profile');
        setProfile(data);
        setFormData({
          name: data.name || '',
          company_website: data.company_website || '',
          company_about: data.company_about || '',
          company_gst: data.company_gst || '',
          company_size: data.company_size || '',
          company_industry: data.company_industry || '',
          company_founded_year: data.company_founded_year || '',
          company_headquarters: data.company_headquarters || '',
          branch_offices: data.branch_offices || '',
          company_culture: data.company_culture || '',
          company_linkedin: data.company_linkedin || '',
          company_facebook: data.company_facebook || '',
          company_instagram: data.company_instagram || '',
          company_benefits: Array.isArray(data.company_benefits)
            ? data.company_benefits
            : (data.company_benefits ? String(data.company_benefits).split(',').filter(Boolean) : []),
        });
      } catch (err) {
        console.error('Failed to fetch profile', err);
      } finally {
        setLoading(false);
      }
    };
    fetchProfile();
  }, [navigate]);

  // Auto-dismiss the toast
  useEffect(() => {
    if (!message) return;
    const t = setTimeout(() => setMessage(''), 4000);
    return () => clearTimeout(t);
  }, [message]);

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const toggleBenefit = (benefit) => {
    setFormData((prev) => ({
      ...prev,
      company_benefits: prev.company_benefits.includes(benefit)
        ? prev.company_benefits.filter((b) => b !== benefit)
        : [...prev.company_benefits, benefit],
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage('');
    try {
      const response = await api.put('/profile', formData);
      setProfile(response.data.user);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      setMessage('Company profile updated successfully!');
    } catch (err) {
      setMessage(err.response?.data?.message || 'Failed to update profile.');
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (file) => {
    if (!file) return;
    setUploadingLogo(true);
    setMessage('');
    const data = new FormData();
    data.append('logo', file);
    try {
      const response = await api.post('/profile/company-logo', data, { headers: { 'Content-Type': 'multipart/form-data' } });
      setMessage('Company logo uploaded successfully!');
      setLogoError(false);
      setProfile({ ...profile, company_logo: response.data.logo_url });
    } catch (err) {
      setMessage(err.response?.data?.message || 'Failed to upload logo.');
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleCoverUpload = async (file) => {
    if (!file) return;
    setUploadingCover(true);
    setMessage('');
    const data = new FormData();
    data.append('banner', file); // backend validator expects the field named 'banner'
    try {
      const response = await api.post('/profile/company-banner', data, { headers: { 'Content-Type': 'multipart/form-data' } });
      setMessage('Cover banner uploaded successfully!');
      setCoverError(false);
      setProfile({ ...profile, company_cover_banner: response.data.banner_url });
    } catch (err) {
      setMessage(err.response?.data?.message || 'Failed to upload cover banner.');
    } finally {
      setUploadingCover(false);
    }
  };

  if (loading) return <div className="p-10 text-center text-gray-500">Loading...</div>;

  const logoSrc = resolveMediaUrl(profile?.company_logo);
  const coverSrc = resolveMediaUrl(profile?.company_cover_banner);
  const isVerified = profile?.company_verified_status === 'verified';
  const isSuccess = /success/i.test(message);
  const facts = [
    formData.company_industry,
    formData.company_size && `${formData.company_size} employees`,
    formData.company_headquarters,
  ].filter(Boolean);

  return (
    <div className="min-h-screen bg-[#FDF8FF]">
      <Navbar user={user} />

      {message && (
        <div
          className={`fixed top-24 right-4 z-[60] max-w-xs rounded-xl px-4 py-3 text-sm font-medium shadow-xl ${
            isSuccess ? 'bg-[#ECFDF5] text-[#047857] border border-[#A7F3D0]' : 'bg-red-50 text-red-600 border border-red-100'
          }`}
        >
          {message}
        </div>
      )}

      <div className="flex">
        <EmployerSidebar user={user} />

        <div className="flex-1 min-w-0 lg:ml-16 lg:peer-hover:ml-64 transition-all duration-200 ease-in-out">
          <div className="max-w-6xl mx-auto px-4 py-6 sm:py-8 space-y-6">
            {/* Mobile-only: nav drawer */}
            <div className="lg:hidden">
              <button
                type="button"
                onClick={() => setShowFiltersMobile((s) => !s)}
                className="w-full bg-white rounded-xl shadow p-4 flex items-center justify-between font-semibold text-[#0D1B3D]"
              >
                <span>☰ Menu</span>
                <span className="text-sm text-gray-400 inline-flex items-center gap-1">
                  {showFiltersMobile ? 'Hide' : 'Show'}
                  <ChevronDownIcon className={`transition-transform ${showFiltersMobile ? 'rotate-180' : ''}`} />
                </span>
              </button>
              {showFiltersMobile && <div className="mt-3"><EmployerSidebar user={user} mobile /></div>}
            </div>

            {/* Header: cover, logo, name */}
            <section className="bg-white border border-purple-100 rounded-3xl shadow-md shadow-purple-500/10 overflow-hidden">
              <div className="relative h-36 sm:h-48 bg-gradient-to-r from-[#C4B5FD] via-[#DDD6FE] to-[#F9A8D4]">
                {coverSrc && !coverError && (
                  <img src={coverSrc} alt="Cover banner" className="absolute inset-0 w-full h-full object-cover" onError={() => setCoverError(true)} />
                )}
                <label className="absolute right-3 top-3 cursor-pointer bg-white/90 hover:bg-white text-[#7C3AED] text-xs font-bold px-3.5 py-2 rounded-full shadow-md transition">
                  {uploadingCover ? 'Uploading…' : coverSrc && !coverError ? 'Change cover' : 'Add cover'}
                  <input
                    type="file" accept=".jpg,.jpeg,.png" className="hidden" disabled={uploadingCover}
                    onChange={(e) => { handleCoverUpload(e.target.files[0]); e.target.value = ''; }}
                  />
                </label>
              </div>

              <div className="px-5 sm:px-8 pb-6">
                <div className="flex flex-col sm:flex-row sm:items-start gap-4 -mt-12 sm:-mt-14">
                  <div className="relative shrink-0 w-24 h-24 sm:w-28 sm:h-28 rounded-2xl bg-white p-1 shadow-lg border border-purple-100">
                    {logoSrc && !logoError ? (
                      <img src={logoSrc} alt="Company logo" className="w-full h-full rounded-xl object-cover" onError={() => setLogoError(true)} />
                    ) : (
                      <div className={`w-full h-full rounded-xl ${GRADIENT} text-white flex items-center justify-center text-3xl font-bold`}>
                        {getInitials(formData.name)}
                      </div>
                    )}
                    <label
                      title="Change logo"
                      className="absolute -bottom-2 -right-2 w-8 h-8 rounded-full bg-white border border-purple-100 text-[#7C3AED] shadow-md flex items-center justify-center cursor-pointer hover:bg-purple-50 transition"
                    >
                      {uploadingLogo ? (
                        <span className="text-[10px] font-bold">…</span>
                      ) : (
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
                          <path d="M12 20h9" /><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" />
                        </svg>
                      )}
                      <input
                        type="file" accept=".jpg,.jpeg,.png" className="hidden" disabled={uploadingLogo}
                        onChange={(e) => { handleLogoUpload(e.target.files[0]); e.target.value = ''; }}
                      />
                    </label>
                  </div>

                  <div className="flex-1 min-w-0 sm:pt-16">
                    <h1 className="text-2xl sm:text-3xl font-bold truncate bg-gradient-to-r from-[#5B2EFF] via-[#8B5CF6] to-[#FF4D8D] bg-clip-text text-transparent">
                      {formData.name || 'Your company'}
                    </h1>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {facts.length > 0 ? (
                        facts.map((f) => (
                          <span key={f} className="text-xs font-semibold px-3 py-1 rounded-full bg-purple-50 text-[#7C3AED]">{f}</span>
                        ))
                      ) : (
                        <span className="text-sm text-gray-500">Add your industry, size and location below.</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </section>

            <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_300px] gap-6 items-start">
              {/* Form sections */}
              <form id="company-form" onSubmit={handleSubmit} className="space-y-6 min-w-0">
                <SectionCard title="About your company" description="This is what job seekers read on your job pages.">
                  <Field label="Company name">
                    <input type="text" name="name" value={formData.name} onChange={handleChange} placeholder="Company or contact name" className={INPUT} />
                  </Field>
                  <Field label="Website">
                    <input type="text" name="company_website" value={formData.company_website} onChange={handleChange} placeholder="https://company.com" className={INPUT} />
                  </Field>
                  <Field label="About the company">
                    <textarea name="company_about" value={formData.company_about} onChange={handleChange} rows={4} placeholder="What does your company do?" className={INPUT} />
                  </Field>
                  <Field label="Company culture">
                    <textarea name="company_culture" value={formData.company_culture} onChange={handleChange} rows={3} placeholder="What is it like to work here?" className={INPUT} />
                  </Field>
                </SectionCard>

                <SectionCard title="Company details">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <Field label="Industry">
                      <input type="text" name="company_industry" value={formData.company_industry} onChange={handleChange} placeholder="e.g. IT Services" className={INPUT} />
                    </Field>
                    <Field label="Company size">
                      <select name="company_size" value={formData.company_size} onChange={handleChange} className={INPUT}>
                        <option value="">Select size</option>
                        <option value="1-10">1-10 employees</option>
                        <option value="11-50">11-50 employees</option>
                        <option value="51-200">51-200 employees</option>
                        <option value="201-500">201-500 employees</option>
                        <option value="500+">500+ employees</option>
                      </select>
                    </Field>
                    <Field label="Founded year">
                      <input type="number" name="company_founded_year" value={formData.company_founded_year} onChange={handleChange} placeholder="e.g. 2004" min="1800" max="2100" className={INPUT} />
                    </Field>
                    <Field label="GST number">
                      <input type="text" name="company_gst" value={formData.company_gst} onChange={handleChange} className={INPUT} />
                    </Field>
                    <Field label="Headquarters">
                      <input type="text" name="company_headquarters" value={formData.company_headquarters} onChange={handleChange} placeholder="e.g. Bangalore, India" className={INPUT} />
                    </Field>
                    <Field label="Branch offices" hint="Not saved yet: the backend needs a branch_offices column and validation rule in ProfileController@update.">
                      <input type="text" name="branch_offices" value={formData.branch_offices} onChange={handleChange} placeholder="Comma separated" className={INPUT} />
                    </Field>
                  </div>
                </SectionCard>

                <SectionCard title="Benefits" description="Select everything you offer.">
                  <div className="flex flex-wrap gap-2">
                    {BENEFIT_OPTIONS.map((benefit) => {
                      const selected = formData.company_benefits.includes(benefit);
                      return (
                        <button
                          key={benefit}
                          type="button"
                          onClick={() => toggleBenefit(benefit)}
                          className={`px-3.5 py-1.5 rounded-full text-sm font-medium border transition-all duration-200 ${
                            selected
                              ? `${GRADIENT} text-white border-transparent shadow-md shadow-purple-500/25`
                              : 'bg-white text-[#0D1B3D] border-gray-200 hover:border-[#8B5CF6] hover:bg-purple-50 hover:text-[#7C3AED]'
                          }`}
                        >
                          {selected ? '✔ ' : ''}{benefit}
                        </button>
                      );
                    })}
                  </div>
                </SectionCard>

                <SectionCard title="Social links">
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <Field label="LinkedIn">
                      <input type="text" name="company_linkedin" value={formData.company_linkedin} onChange={handleChange} placeholder="LinkedIn URL" className={INPUT} />
                    </Field>
                    <Field label="Facebook">
                      <input type="text" name="company_facebook" value={formData.company_facebook} onChange={handleChange} placeholder="Facebook URL" className={INPUT} />
                    </Field>
                    <Field label="Instagram">
                      <input type="text" name="company_instagram" value={formData.company_instagram} onChange={handleChange} placeholder="Instagram URL" className={INPUT} />
                    </Field>
                  </div>
                </SectionCard>
              </form>

              {/* Right rail: verification + save */}
              <aside className="space-y-4 lg:sticky lg:top-24">
                <div className="bg-white border border-purple-100 rounded-2xl shadow-sm p-5">
                  <div className="flex items-center gap-3">
                    <span className={`w-10 h-10 shrink-0 rounded-full flex items-center justify-center ${isVerified ? 'bg-[#ECFDF5] text-[#10B981]' : 'bg-purple-50 text-[#7C3AED]'}`}>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5">
                        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                        {isVerified && <path d="m9 12 2 2 4-4" />}
                      </svg>
                    </span>
                    <div>
                      <p className="text-sm font-bold text-[#0D1B3D]">{isVerified ? 'Verified employer' : 'Not verified yet'}</p>
                      <p className="text-xs text-gray-500">
                        {isVerified ? 'Your company is verified.' : 'Verified employers can post jobs.'}
                      </p>
                    </div>
                  </div>
                  <Link
                    to="/company/verification"
                    className="mt-4 block text-center text-sm font-bold text-[#7C3AED] border border-[#8B5CF6]/40 rounded-full py-2 hover:bg-purple-50 transition"
                  >
                    {isVerified ? 'View details' : 'Get verified'}
                  </Link>
                </div>

                <div className="bg-white border border-purple-100 rounded-2xl shadow-sm p-5">
                  <p className="text-sm font-bold text-[#0D1B3D]">Save your changes</p>
                  <p className="text-xs text-gray-500 mt-0.5 mb-4">Logo and cover upload as soon as you choose a file.</p>
                  <button
                    type="submit"
                    form="company-form"
                    disabled={saving}
                    className={`w-full ${GRADIENT} text-white py-2.5 rounded-full text-sm font-bold shadow-lg shadow-purple-500/25 hover:shadow-xl hover:shadow-purple-500/35 hover:scale-[1.02] active:scale-[0.99] transition-all duration-200 disabled:opacity-50`}
                  >
                    {saving ? 'Saving...' : 'Save company profile'}
                  </button>
                </div>
              </aside>
            </div>
          </div>

          <Footer />
        </div>
      </div>
    </div>
  );
}

export default CompanyProfile;